import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';

const PAGE_SIZES = [10, 25, 50];

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, totalPages: 1 });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const searchTimer = useRef(null);

  const [detailModal, setDetailModal] = useState(null); // single payment row

  const fetchPayments = useCallback(async (page = 1, limit = 10, q = '') => {
    try {
      const { data } = await api.get('/payment/admin/payments', { params: { page, limit, search: q } });
      setPayments(data.payments);
      setPagination(data.pagination);
    } catch {}
  }, []);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(val);
      fetchPayments(1, pagination.limit, val);
    }, 400);
  };

  const goToPage = (p) => fetchPayments(p, pagination.limit, search);
  const changeLimit = (l) => fetchPayments(1, l, search);

  const openDetail = (payment) => setDetailModal(payment);

  const { page, totalPages, total, limit } = pagination;
  const startRow = total === 0 ? 0 : (page - 1) * limit + 1;
  const endRow = Math.min(page * limit, total);
  const pageNums = [];
  for (let i = Math.max(1, page - 2); i <= Math.min(totalPages, page + 2); i++) pageNums.push(i);

  return (
    <div>
      <div className="table-card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <span className="card-title">💳 Payments</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={st.label}>Show</span>
              <select style={{ ...st.input, padding: '7px 10px', cursor: 'pointer' }}
                value={limit} onChange={(e) => changeLimit(Number(e.target.value))}>
                {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={{ position: 'relative' }}>
              <span style={st.searchIcon}>🔍</span>
              <input
                style={{ ...st.input, paddingLeft: 30, width: 220 }}
                placeholder="Search name or email..."
                value={searchInput}
                onChange={handleSearchChange}
              />
            </div>
          </div>
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>User</th>
                <th>Email</th>
                <th>Plan</th>
                <th>Amount</th>
                <th>Currency</th>
                <th>Status</th>
                <th>Date</th>
                <th>History</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', color: '#a0aec0', padding: 32 }}>No payments found</td></tr>
              ) : payments.map((p, i) => (
                <tr key={p.id}>
                  <td>{startRow + i}</td>
                  <td>{p.user?.name || '—'}</td>
                  <td>{p.user?.email || '—'}</td>
                  <td>{p.plan?.name || '—'}</td>
                  <td>{p.amount}</td>
                  <td>{p.currency}</td>
                  <td>
                    <span style={{
                      display: 'inline-block', borderRadius: 4, padding: '2px 8px', fontSize: 12,
                      background: p.status === 'paid' ? '#dcfce7' : '#fee2e2',
                      color: p.status === 'paid' ? '#16a34a' : '#dc2626',
                    }}>{p.status}</span>
                  </td>
                  <td style={{ fontSize: 12, color: '#718096' }}>{new Date(p.created_at).toLocaleDateString()}</td>
                  <td>
                    <button className="edit-btn" onClick={() => openDetail(p)}>View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 0 && (
          <div style={st.paginationWrap}>
            <span style={st.pageInfo}>Showing {startRow}–{endRow} of {total}</span>
            <div style={st.pageButtons}>
              <button style={st.pageBtn} disabled={page === 1} onClick={() => goToPage(1)}>«</button>
              <button style={st.pageBtn} disabled={page === 1} onClick={() => goToPage(page - 1)}>‹</button>
              {pageNums.map(n => (
                <button key={n} style={{ ...st.pageBtn, ...(n === page ? st.pageBtnActive : {}) }}
                  onClick={() => goToPage(n)}>{n}</button>
              ))}
              <button style={st.pageBtn} disabled={page === totalPages} onClick={() => goToPage(page + 1)}>›</button>
              <button style={st.pageBtn} disabled={page === totalPages} onClick={() => goToPage(totalPages)}>»</button>
            </div>
          </div>
        )}
      </div>

      {/* Payment Detail Modal */}
      {detailModal && (
        <div style={st.backdrop} onClick={() => setDetailModal(null)}>
          <div style={st.modalBox} onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={st.modalHeader}>
              <span style={st.modalTitle}>💳 Payment Detail</span>
              <button style={st.closeBtn} onClick={() => setDetailModal(null)}>✕</button>
            </div>

            <div style={{ padding: '20px 24px 24px' }}>

              {/* Status banner */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                background: detailModal.status === 'paid' ? '#f0fdf4' : '#fef2f2',
                border: `1px solid ${detailModal.status === 'paid' ? '#bbf7d0' : '#fecaca'}`,
                borderRadius: 10, padding: '14px 18px', marginBottom: 20,
              }}>
                <span style={{ fontSize: 28 }}>{detailModal.status === 'paid' ? '✅' : '❌'}</span>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: detailModal.status === 'paid' ? '#16a34a' : '#dc2626' }}>
                    {detailModal.currency} {detailModal.amount}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                    {detailModal.status === 'paid' ? 'Payment Successful' : 'Payment Failed'}
                  </div>
                </div>
                <span style={{
                  marginLeft: 'auto', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                  background: detailModal.status === 'paid' ? '#dcfce7' : '#fee2e2',
                  color: detailModal.status === 'paid' ? '#16a34a' : '#dc2626',
                }}>{detailModal.status.toUpperCase()}</span>
              </div>

              {/* Two-column info grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                {[
                  { label: '👤 Customer', value: detailModal.user?.name || '—' },
                  { label: '📧 Email',    value: detailModal.user?.email || '—' },
                  { label: '📦 Plan',     value: detailModal.plan?.name || '—' },
                  { label: '📅 Date',     value: new Date(detailModal.created_at).toLocaleString() },
                ].map(({ label, value }) => (
                  <div key={label} style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 14px', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 14, color: '#1e293b', fontWeight: 500, wordBreak: 'break-all' }}>{value}</div>
                  </div>
                ))}
              </div>

              {/* Session ID full width */}
              <div style={{ background: '#f1f5f9', borderRadius: 8, padding: '12px 14px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>🔑 Session ID</div>
                <div style={{ fontSize: 12, color: '#475569', fontFamily: 'monospace', wordBreak: 'break-all' }}>{detailModal.stripe_session_id}</div>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const st = {
  label:          { fontSize: 13, color: '#4a5568', fontWeight: 500 },
  input:          { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, outline: 'none' },
  searchIcon:     { position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 13, pointerEvents: 'none' },
  paginationWrap: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 16, paddingTop: 14, borderTop: '1px solid #e2e8f0' },
  pageInfo:       { fontSize: 13, color: '#718096' },
  pageButtons:    { display: 'flex', gap: 4 },
  pageBtn:        { minWidth: 34, height: 34, padding: '0 10px', border: '1px solid #e2e8f0', background: '#fff', borderRadius: 5, cursor: 'pointer', fontSize: 13, color: '#4a5568' },
  pageBtnActive:  { background: '#2563eb', color: '#fff', borderColor: '#2563eb', fontWeight: 600 },
  backdrop:       { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modalBox:       { background: '#fff', borderRadius: 10, width: '100%', maxWidth: 640, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' },
  modalHeader:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, background: '#fff', zIndex: 1 },
  modalTitle:     { fontSize: 15, fontWeight: 600, color: '#2d3748' },
  closeBtn:       { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#718096', lineHeight: 1 },
};
